import { useEffect, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { LightBaker } from './lightBaker/LightBaker.jsx';
import { prepareBakedScene, disposeTree } from './bakedModel.jsx';
import { encodeLightmap, exportBakedGlb } from './designExport.jsx';

// «رندرِ واقعی روی همین دستگاه»: نور با کارت گرافیکِ خودِ کاربر پخته می‌شود (بدونِ سرور، بدونِ ارسال و دانلود).
// کاربر از همان لحظه‌ی اول صحنه را با نورِ پخته‌شده می‌بیند و تصویر مرتب تمیزتر و واقعی‌تر می‌شود.
//
// cloneRef.current(): یک کپیِ آماده از ساختمان (همان که برای سرور فرستاده می‌شود — SceneExporter)
// onStatus({ phase: 'prepare' | 'baking' | 'done' | 'error', samples, target, elapsedMs, stats, message })
// paused: مکث (کارت گرافیک آزاد می‌شود). finishSignal: با هر افزایش «همین حالا تمامش کن» (حذفِ نویز و پایان)
// saveRef.current(onStep) (بعد از پایان): فایل‌های «ارسال فایل روی سرور» → { glb, lightmap (JPEG), intensity, samples, size }

const DEFAULT_SAMPLES = 1024;
const REPORT_MS = 1000; // هر گزارش کلِ صفحه را دوباره می‌سازد — زیاد نباشد

export function LocalBakeView({ cloneRef, saveRef, showCeiling, exposure = 1, paused, finishSignal = 0, samples = DEFAULT_SAMPLES, onStatus }) {
  const { gl, camera } = useThree();
  const groupRef = useRef(null);
  // حرکتِ دوربین (راه رفتن/چرخاندن): پختن همان لحظه کنار می‌رود تا حرکت روان بماند؛ کمی بعد از ایستادن ادامه می‌یابد
  const moveRef = useRef({ p: null, q: null, last: -1e9 });
  const st = useRef({});
  const onStatusRef = useRef(onStatus);
  onStatusRef.current = onStatus;
  const showCeilingRef = useRef(showCeiling);
  showCeilingRef.current = showCeiling;
  const exposureRef = useRef(exposure);
  exposureRef.current = exposure;
  const pausedRef = useRef(paused);
  pausedRef.current = paused;

  const report = (extra) => {
    const s = st.current;
    const b = s.baker;
    if (onStatusRef.current) {
      onStatusRef.current({
        samples: b ? b.samples : 0,
        target: b ? b.targetSamples : samples,
        elapsedMs: s.elapsed || 0,
        stats: b ? b.stats : null,
        ...extra,
      });
    }
  };

  useEffect(() => {
    if (!saveRef) return undefined;
    saveRef.current = async (onStep) => {
      const { baker, root } = st.current;
      if (!baker || !root || !baker.finished || !baker.lightMap) throw new Error('not-finished');
      if (onStep) onStep('lightmap');
      const lightmap = await encodeLightmap(gl, baker.lightMap, baker.size);
      if (onStep) onStep('model');
      const glb = await exportBakedGlb(root);
      return { glb, lightmap: lightmap.blob, intensity: lightmap.intensity, samples: baker.samples, size: baker.size };
    };
    return () => {
      saveRef.current = null;
    };
  }, [saveRef, gl]);

  // اگر مرورگر ارتباط با کارت گرافیک را قطع کند (Context Lost)، کار متوقف و به کاربر گفته شود
  useEffect(() => {
    const canvas = gl.domElement;
    const onLost = () => {
      st.current.lost = true;
      report({ phase: 'error', message: 'context-lost' });
    };
    canvas.addEventListener('webglcontextlost', onLost);
    return () => canvas.removeEventListener('webglcontextlost', onLost);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gl]);

  useEffect(() => {
    let cancelled = false;
    report({ phase: 'prepare' });
    // یک لحظه صبر تا پیامِ «آماده‌سازی» دیده شود (آماده‌سازی چند صد میلی‌ثانیه صفحه را مشغول می‌کند)
    const timer = setTimeout(() => {
      if (cancelled) return;
      try {
        const root = cloneRef.current ? cloneRef.current() : null;
        if (!root) throw new Error('no-scene');
        const baker = new LightBaker(gl, root, { size: 2048, samples, bounces: 4 });
        baker.prepare();
        const { ceilings, materials } = prepareBakedScene(root, baker.lightMap, exposureRef.current);
        ceilings.forEach((c) => {
          c.visible = showCeilingRef.current;
        });
        if (groupRef.current) groupRef.current.add(root);
        st.current = { baker, root, ceilings, materials, elapsed: 0, lastReport: 0 };
        report({ phase: 'baking' });
      } catch (e) {
        report({ phase: 'error', message: String(e?.message || e) });
      }
    }, 80);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      const { baker, root } = st.current;
      if (root) {
        if (root.parent) root.parent.remove(root);
        disposeTree(root);
      }
      if (baker) baker.dispose();
      st.current = {};
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // «همین حالا تمامش کن»
  const lastFinishRef = useRef(finishSignal);
  useEffect(() => {
    if (finishSignal === lastFinishRef.current) return;
    lastFinishRef.current = finishSignal;
    const { baker } = st.current;
    if (baker && !baker.finished && baker.samples > 0) {
      baker.finish();
      report({ phase: 'done' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finishSignal]);

  useEffect(() => {
    (st.current.ceilings || []).forEach((c) => {
      c.visible = showCeiling;
    });
  }, [showCeiling]);

  useEffect(() => {
    (st.current.materials || []).forEach((m) => {
      m.lightMapIntensity = exposure;
    });
  }, [exposure]);

  useFrame((_, delta) => {
    const s = st.current;
    const { baker } = s;
    if (!baker || baker.finished || pausedRef.current || s.lost) return;
    const mv = moveRef.current;
    const now = performance.now();
    if (!mv.p) {
      mv.p = camera.position.clone();
      mv.q = camera.quaternion.clone();
    }
    if (mv.p.distanceToSquared(camera.position) > 0.0001 || mv.q.angleTo(camera.quaternion) > 0.002) {
      mv.p.copy(camera.position);
      mv.q.copy(camera.quaternion);
      mv.last = now;
    }
    if (now - mv.last < 400) return;
    const dt = delta * 1000;
    s.elapsed += Math.min(dt, 250);
    baker.step(dt);
    if (baker.samples >= baker.targetSamples) {
      baker.finish();
      report({ phase: 'done' });
      return;
    }
    if (s.elapsed - s.lastReport > REPORT_MS) {
      s.lastReport = s.elapsed;
      report({ phase: 'baking' });
    }
  });

  return <group ref={groupRef} userData={{ khNoExport: true }} />;
}
